/**
 * Le cifre di chi amministra: pacchetti per tappa, anomalie, cancello, consegne.
 *
 * **Un numero senza l'elenco dietro e' una domanda in piu'.** «Tre pacchetti
 * aspettano il cancello» chiama subito «quali?», e chi amministra per matrice
 * non vede la coda di verifica dove andrebbe a cercarli: ogni cifra si apre
 * e mostra i pacchetti che la compongono. Dove il permesso c'e', la riga e'
 * un collegamento; dove non c'e', e' un nome — la matrice non cambia per una
 * schermata.
 *
 * **Il periodo filtra gli eventi, non la fotografia.** Le anomalie e le
 * consegne hanno una data; la tappa di un pacchetto no. Cambiare periodo
 * cambia le prime e lascia ferma la seconda, ed e' giusto cosi': «quanti
 * pacchetti sono fermi» non ha una settimana di riferimento.
 *
 * **Se non si sa, non si scrive zero.** Un guasto della lettura lascia un
 * avviso al posto delle cifre: «nessun pacchetto fermo» e' una buona notizia,
 * e darla per un errore di rete e' il modo piu' rapido di far smettere di
 * guardare qui.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ServiceError } from '../api'
import { EXPORT_RECEIPT_READ, REVIEW_QUEUE, type Whoami } from '../auth'
import { Severity } from '../design/indicators'
import { decimal, moment } from '../format'
import { explainService } from '../i18n/explain'
import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '../i18n/messages'
import { PERIODS, readOverview, type Overview, type Package, type Period } from '../overview'

type Translate = ReturnType<typeof useT>

/** Il nome di ogni tappa, a parole. */
const STAGE: Record<string, MessageKey> = {
  acquiring: 'figures.acquiring',
  failed: 'figures.failed',
  in_review: 'figures.inReview',
  awaiting_gate: 'figures.awaitingGate',
  sealed: 'figures.sealed',
  delivered: 'figures.delivered',
  accepted: 'figures.accepted',
  rejected: 'figures.rejected',
}

/** Il tono della tappa: il colore e' il dato, e il nome viaggia con lui. */
const TONE: Record<string, string> = {
  failed: 'sev-4',
  rejected: 'sev-4',
  awaiting_gate: 'sev-2',
  sealed: 'ref',
  accepted: 'sev-0',
}

const PERIOD: Record<Period, MessageKey> = {
  week: 'figures.week',
  month: 'figures.month',
  all: 'figures.all',
}

export function Figures({ who }: { who: Whoami }): JSX.Element {
  const t = useT()
  const [period, setPeriod] = useState<Period>('week')
  const [seen, setSeen] = useState<Overview | null>(null)
  const [failure, setFailure] = useState<ServiceError | Error | null>(null)

  /*
    Senza `t` fra le dipendenze: `useT()` cambia identita' a ogni render, e
    una lettura che dipende da lei si riarma da sola all'infinito. Il guasto
    si traduce dipingendo.
  */
  useEffect(() => {
    let alive = true
    setSeen(null)
    setFailure(null)
    void readOverview(period)
      .then((found) => {
        if (alive) setSeen(found)
      })
      .catch((error: unknown) => {
        if (alive) setFailure(error instanceof Error ? error : new Error(String(error)))
      })
    return () => {
      alive = false
    }
  }, [period])

  const wrong =
    failure === null
      ? null
      : failure instanceof ServiceError
        ? explainService(t, failure.code, failure.message)
        : t('figures.unreachable')

  return (
    <section className="cifre" aria-label={t('figures.title')}>
      <div className="cifre-testa">
        <h3 className="cifre-titolo">{t('figures.title')}</h3>
        <div role="group" aria-label={t('figures.period')} className="cifre-periodo">
          {PERIODS.map((one) => (
            <button
              key={one}
              type="button"
              aria-pressed={one === period}
              onClick={() => setPeriod(one)}
            >
              {t(PERIOD[one])}
            </button>
          ))}
        </div>
      </div>
      {wrong !== null && <p role="alert">{wrong}</p>}
      {wrong === null && seen === null && (
        <p className="tenue" role="status">
          {t('figures.reading')}
        </p>
      )}
      {seen !== null && <Board seen={seen} who={who} t={t} />}
    </section>
  )
}

function Board({ seen, who, t }: { seen: Overview; who: Whoami; t: Translate }): JSX.Element {
  const stages = seen.packages.filter((one) => one.count > 0 || one.stage === 'awaiting_gate')
  const levels = seen.findings.filter((one) => one.count > 0)
  const canOpen = who.may.includes(REVIEW_QUEUE)
  const canReceipts = who.may.includes(EXPORT_RECEIPT_READ)
  return (
    <>
      <p className="tenue cifre-data" role="status">
        {t('figures.computedAt', { when: moment(seen.computed_at) })}
      </p>

      <h4 className="cifre-gruppo">{t('figures.packages')}</h4>
      {stages.length === 0 ? (
        <p className="tenue">{t('figures.nonePackages')}</p>
      ) : (
        <div className="cifre-riga">
          {stages.map((one) => (
            <Figure
              key={one.stage}
              value={one.count}
              label={t(STAGE[one.stage] ?? 'figures.acquiring')}
              tone={one.count > 0 ? TONE[one.stage] : undefined}
              packages={one.packages}
              linked={canOpen}
              t={t}
            />
          ))}
        </div>
      )}

      <h4 className="cifre-gruppo">{t('figures.findings')}</h4>
      {levels.length === 0 ? (
        <p className="tenue">{t('figures.noneFindings')}</p>
      ) : (
        <div className="cifre-riga">
          {levels.map((one) => (
            <div key={one.level} className="cifra">
              <span className="cifra-numero t-num">{decimal(one.count)}</span>
              <Severity level={one.level} />
            </div>
          ))}
          <div className="cifra">
            <span className="cifra-numero t-num">{decimal(seen.findings_total)}</span>
            <span className="cifra-nome">{t('figures.total')}</span>
          </div>
        </div>
      )}

      <h4 className="cifre-gruppo">{t('figures.deliveries')}</h4>
      {seen.deliveries.submitted === 0 ? (
        <p className="tenue">{t('figures.noneDeliveries')}</p>
      ) : (
        <div className="cifre-riga">
          <Figure
            value={seen.deliveries.submitted}
            label={t('figures.submitted')}
            packages={seen.deliveries.packages}
            linked={canReceipts}
            to="/exports"
            t={t}
          />
          <Figure
            value={seen.deliveries.with_receipt}
            label={t('figures.withReceipt')}
            tone={seen.deliveries.with_receipt < seen.deliveries.submitted ? 'sev-2' : undefined}
            t={t}
          />
          <Figure
            value={seen.deliveries.accepted}
            label={t('figures.acceptedDeliveries')}
            tone={seen.deliveries.accepted > 0 ? 'sev-0' : undefined}
            t={t}
          />
          <Figure
            value={seen.deliveries.rejected}
            label={t('figures.rejectedDeliveries')}
            tone={seen.deliveries.rejected > 0 ? 'sev-4' : undefined}
            t={t}
          />
        </div>
      )}
    </>
  )
}

/**
 * Una cifra col suo nome e, se ha un elenco dietro, l'elenco che si apre.
 *
 * Il numero e' grande e il nome piccolo, perche' si scorre con l'occhio: ma
 * il nome c'e' sempre, e il tono da solo non dice niente a uno su dodici.
 */
function Figure({
  value,
  label,
  tone,
  packages,
  linked = false,
  to,
  t,
}: {
  value: number
  label: string
  tone?: string | undefined
  packages?: Package[] | undefined
  linked?: boolean
  to?: string | undefined
  t: Translate
}): JSX.Element {
  const style = tone === undefined ? undefined : { color: `var(--${tone})` }
  if (packages === undefined || packages.length === 0) {
    return (
      <div className="cifra">
        <span className="cifra-numero t-num" style={style}>
          {decimal(value)}
        </span>
        <span className="cifra-nome">{label}</span>
      </div>
    )
  }
  return (
    <details className="cifra">
      <summary>
        <span className="cifra-numero t-num" style={style}>
          {decimal(value)}
        </span>
        <span className="cifra-nome">{label}</span>
      </summary>
      <ul className="cifra-elenco" aria-label={t('figures.behind')}>
        {packages.map((one) => (
          <li key={one.inspection_id} className="t-num">
            {linked ? (
              <Link to={to ?? `/inspections/${one.inspection_id}`}>{one.external_inspection_id}</Link>
            ) : (
              one.external_inspection_id
            )}
          </li>
        ))}
      </ul>
    </details>
  )
}
